import { VALTYPE_ANY, VALTYPES_NON_NUMERIC, VALTYPES } from '../../../data/interfaces';
import { TpFilterInstance, TpETLFilterDryRunContext, TpETLFilterType } from './interfaces';
import { addETLDryRunError } from '../etlDryRun';

function isPositiveNaturalNumber(value: string | number): boolean {
  const num = Number(value);
  return !isNaN(num) && num > 0 && Number.isInteger(num);
}

function isValidFraction(value: string | number): boolean {
  const num = Number(value);
  return !isNaN(num) && num > 0 && num <= 1;
}

const filterContains: TpETLFilterType = {
  id: 'contains',
  name: 'Contains',
  description: 'Filters rows where the column value contains a given value',
  compatibleInputDataTypes: VALTYPES_NON_NUMERIC,
  getFilterName: (filter: TpFilterInstance) => {
    return filterContains.name;
  },
  performDryRun: (ctx: TpETLFilterDryRunContext) => {},
};

const filterDoesNotContain: TpETLFilterType = {
  id: 'doesNotContain',
  name: 'Does not contain',
  description: 'Filters rows where the column value does not contain a given value',
  compatibleInputDataTypes: VALTYPES_NON_NUMERIC,
  getFilterName: (filter: TpFilterInstance) => {
    return filterDoesNotContain.name;
  },
  performDryRun: (ctx: TpETLFilterDryRunContext) => {},
};

const filterEquals: TpETLFilterType = {
  id: 'eq',
  name: 'Equals (==)',
  description: 'Filters rows where the column value matches a given value',
  compatibleInputDataTypes: [VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.INTEGER, VALTYPES.TEXT, VALTYPES.CATEGORICAL],
  getFilterName: (filter: TpFilterInstance) => {
    return filterEquals.name;
  },
  performDryRun: (ctx: TpETLFilterDryRunContext) => {},
};

const filterNotEquals: TpETLFilterType = {
  id: 'neq',
  name: 'Not equals (!=)',
  description: 'Filters rows where the column value does not match a given value',
  compatibleInputDataTypes: [VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.INTEGER, VALTYPES.TEXT, VALTYPES.CATEGORICAL],
  getFilterName: (filter: TpFilterInstance) => {
    return filterNotEquals.name;
  },
  performDryRun: (ctx: TpETLFilterDryRunContext) => {},
};

const filterGreaterThanOrEquals: TpETLFilterType = {
  id: 'gte',
  name: 'Greater than or equals (>=)',
  description: 'Filters rows where a numerical column value is greater than or equals to a given value',
  compatibleInputDataTypes: [VALTYPES.NUMBER, VALTYPES.INTEGER],
  getFilterName: (filter: TpFilterInstance) => {
    return filterGreaterThanOrEquals.name;
  },
  performDryRun: (ctx: TpETLFilterDryRunContext) => {},
};

const filterGreaterThan: TpETLFilterType = {
  id: 'gt',
  name: 'Greater than (>)',
  description: 'Filters rows where a numerical column value is greater than a given value',
  compatibleInputDataTypes: [VALTYPES.NUMBER, VALTYPES.INTEGER],
  getFilterName: (filter: TpFilterInstance) => {
    return filterGreaterThan.name;
  },
  performDryRun: (ctx: TpETLFilterDryRunContext) => {},
};

const filterLesserThanOrEquals: TpETLFilterType = {
  id: 'lte',
  name: 'Lesser than or equals (<=)',
  description: 'Filters rows where a numerical column value is lesser than or equals to a given value',
  compatibleInputDataTypes: [VALTYPES.NUMBER, VALTYPES.INTEGER],
  getFilterName: (filter: TpFilterInstance) => {
    return filterLesserThanOrEquals.name;
  },
  performDryRun: (ctx: TpETLFilterDryRunContext) => {},
};

const filterLesserThan: TpETLFilterType = {
  id: 'lt',
  name: 'Lesser than (<)',
  description: 'Filters rows where a numerical column value is lesser than a given value',
  compatibleInputDataTypes: [VALTYPES.NUMBER, VALTYPES.INTEGER],
  getFilterName: (filter: TpFilterInstance) => {
    return filterLesserThan.name;
  },
  performDryRun: (ctx: TpETLFilterDryRunContext) => {},
};

const filterCountSmallest: TpETLFilterType = {
  id: 'countSmallest',
  name: 'Number of smallest values',
  description: 'Filters rows with the smallest values in a column, up to a given count',
  compatibleInputDataTypes: [VALTYPES.NUMBER, VALTYPES.INTEGER],
  getFilterName: (filter: TpFilterInstance) => {
    return filterCountSmallest.name;
  },
  performDryRun: ({ filter, comp, dryRun }: TpETLFilterDryRunContext) => {
    if (filter.value !== '' && !isPositiveNaturalNumber(filter.value)) {
      addETLDryRunError(dryRun, `Invalid count for smallest values filter`, comp.id, filter.id);
    }
  },
};

const filterCountLargest: TpETLFilterType = {
  id: 'countLargest',
  name: 'Number of largest values',
  description: 'Filters rows with the largest values in a column, up to a given count',
  compatibleInputDataTypes: [VALTYPES.NUMBER, VALTYPES.INTEGER],
  getFilterName: (filter: TpFilterInstance) => {
    return filterCountLargest.name;
  },
  performDryRun: ({ filter, comp, dryRun }: TpETLFilterDryRunContext) => {
    if (filter.value !== '' && !isPositiveNaturalNumber(filter.value)) {
      addETLDryRunError(dryRun, `Invalid count for largest values filter`, comp.id, filter.id);
    }
  },
};

const filterFractionSmallest: TpETLFilterType = {
  id: 'fractionSmallest',
  name: 'Fraction of smallest values',
  description: 'Filters rows with the smallest values in a column, up to a given fraction',
  compatibleInputDataTypes: [VALTYPES.NUMBER, VALTYPES.INTEGER],
  getFilterName: (filter: TpFilterInstance) => {
    return filterFractionSmallest.name;
  },
  performDryRun: ({ filter, comp, dryRun }: TpETLFilterDryRunContext) => {
    if (filter.value !== '' && !isValidFraction(filter.value)) {
      addETLDryRunError(dryRun, `Invalid fraction for smallest values filter`, comp.id, filter.id);
    }
  },
};

const filterFractionLargest: TpETLFilterType = {
  id: 'fractionLargest',
  name: 'Fraction of largest values',
  description: 'Filters rows with the largest values in a column, up to a given fraction',
  compatibleInputDataTypes: [VALTYPES.NUMBER, VALTYPES.INTEGER],
  getFilterName: (filter: TpFilterInstance) => {
    return filterFractionLargest.name;
  },
  performDryRun: ({ filter, comp, dryRun }: TpETLFilterDryRunContext) => {
    if (filter.value !== '' && !isValidFraction(filter.value)) {
      addETLDryRunError(dryRun, `Invalid fraction for largest values filter`, comp.id, filter.id);
    }
  },
};

// The list of all available filter types
export const filterTypesList = [
  filterContains,
  filterDoesNotContain,
  filterEquals,
  filterNotEquals,
  filterGreaterThanOrEquals,
  filterGreaterThan,
  filterLesserThanOrEquals,
  filterLesserThan,
  filterCountSmallest,
  filterCountLargest,
  filterFractionSmallest,
  filterFractionLargest,
];

const filterTypesMap: { [id: string]: TpETLFilterType } = {};

for (const filter of filterTypesList) filterTypesMap[filter.id] = filter;

export function getFilterTypeInfo(filterType: string) {
  if (!filterTypesMap[filterType]) {
    debugger;
    throw `Invalid filter type: ${filterType}`;
  }
  return filterTypesMap[filterType];
}

export const defaultFilterType = filterContains;

export function filterHasCompatibleSourceColumn(filterTypeId: string, sourceDataType: string): boolean {
  if (!sourceDataType) return false; // No source data type provided, cannot be compatible
  const filterType = getFilterTypeInfo(filterTypeId);
  if (filterType.compatibleInputDataTypes == VALTYPE_ANY) return true;
  return filterType.compatibleInputDataTypes.includes(sourceDataType);
}

export function getDefaultFilterType(sourceDataType: string): TpETLFilterType {
  const compatibleFilterTypes = filterTypesList.filter((filterType) =>
    filterHasCompatibleSourceColumn(filterType.id, sourceDataType),
  );
  return compatibleFilterTypes.length > 0 ? compatibleFilterTypes[0] : defaultFilterType;
}
